const baseFontSize = 16

function pxToRem(px) {
  return `${px / baseFontSize}rem`
}

/** @type {import('tailwindcss').Config} */
module.exports = {
  darkMode: "class",
  theme: {
    colors: {
      transparent: "transparent",
      current: "currentColor",
      black: {
        DEFAULT: "#171A1B",
        10: "rgba(23,26,27,0.1)",
        30: "rgba(23,26,27,0.3)",
        50: "rgba(23,26,27,0.5)",
        70: "rgba(23,26,27,0.7)",
      },
      white: {
        DEFAULT: "#ffffff",
        10: "rgba(255,255,255,0.1)",
        30: "rgba(255,255,255,0.3)",
        50: "rgba(255,255,255,0.5)",
        70: "rgba(255,255,255,0.7)",
      },
      pale: "#fefbf9",
      tan: "#f6efea",
      wine: "#2C0719",
      grey: {
        DEFAULT: "#40383c",
        200: "#d9d7d8",
        300: "#9f9b9d",
        400: "#40383c",
      },
      green: {
        DEFAULT: "#4faa13",
        200: "#cce4b9",
        300: "#9dd482",
        400: "#4faa13",
        500: "#556e26",
      },
      yellow: {
        DEFAULT: "#ffb600",
        200: "#ffeeb3",
        300: "#ffe36e",
        400: "#ffb600",
        500: "#c68600",
      },
      orange: {
        DEFAULT: "#ff5C0b",
        200: "#ffd1b9",
        300: "#ffb08a",
        400: "#ff5C0b",
        500: "#b74302",
      },
      pink: {
        DEFAULT: "#e21454",
        200: "#f9c2cb",
        300: "#f597a8",
        400: "#e21454",
        500: "#a52932",
      },
      purple: {
        DEFAULT: "#9a36b2",
        200: "#e4c8f2",
        300: "#c888e2",
        400: "#9a36b2",
        500: "#7c316a",
      },
      blue: {
        DEFAULT: "#00b3b7",
        200: "#a6e5e7",
        300: "#64d2d7",
        400: "#00b3b7",
        500: "#065c63",
      },
      jade: {
        DEFAULT: "#01a179",
        200: "#b3e3d7",
        300: "#67c7af",
        400: "#01a179",
        500: "#017155",
      },
    },
    fontFamily: {
      heading: ["tiempos", "serif"],
      main: ["inter", "sans-serif"],
      camper: ["camper", "serif"],
    },
    fontSize: {
      base: [pxToRem(16), { lineHeight: pxToRem(22) }],
      8: [pxToRem(8), { lineHeight: pxToRem(12) }],
      10: [pxToRem(10), { lineHeight: pxToRem(14) }],
      12: [pxToRem(12), { lineHeight: pxToRem(18) }],
      14: [pxToRem(14), { lineHeight: pxToRem(20) }],
      16: [pxToRem(16), { lineHeight: pxToRem(24) }],
      20: [pxToRem(20), { lineHeight: pxToRem(30) }],
      24: [pxToRem(24), { lineHeight: pxToRem(34) }],
      32: [pxToRem(32), { lineHeight: pxToRem(40) }],
      36: [pxToRem(36), { lineHeight: pxToRem(42) }],
      40: [pxToRem(40), { lineHeight: pxToRem(48) }],
      48: [pxToRem(48), { lineHeight: pxToRem(60) }],
      60: [pxToRem(60), { lineHeight: pxToRem(68) }],
      84: [pxToRem(82), { lineHeight: pxToRem(90) }],
    },
    screens: {
      mobile: { min: "375px" },
      mobileOnly: { min: "375px", max: "767px" },
      tablet: { min: "768px" },
      tabletOnly: { min: "768px", max: "1199px" },
      desktop: { min: "1200px" },
      desktopOnly: { min: "1200px", max: "1399px" },
      desktopPlus: { min: "1400px" },
    },
    spacing: {
      px: "1px",
      0: "0",
      1: pxToRem(1),
      2: pxToRem(2),
      4: pxToRem(4),
      6: pxToRem(6),
      8: pxToRem(8),
      10: pxToRem(10),
      12: pxToRem(12),
      16: pxToRem(16),
      20: pxToRem(20),
      24: pxToRem(24),
      36: pxToRem(36),
      48: pxToRem(48),
      60: pxToRem(60),
      84: pxToRem(84),
      108: pxToRem(108),
      132: pxToRem(132),
      156: pxToRem(156),
    },
    maxHeight: theme => ({
      ...theme("spacing"),
    }),
    minHeight: theme => ({
      ...theme("spacing"),
    }),
    maxWidth: theme => ({
      ...theme("spacing"),
    }),
    minWidth: theme => ({
      ...theme("spacing"),
    }),
    extend: {
      gridColumnEnd: {
        full: "-1",
      },
      boxShadow: {
        0: "0px 0px 2px 0px rgba(0,0,0,0.1)",
        1: "0px 2px 20px 0px rgba(23,26,27,0.05)",
        2: "0px 4px 40px 0px rgba(23,26,27,0.05)",
      },
    },
  },
  corePlugins: {
    container: false,
  },
  plugins: [require("@tailwindcss/aspect-ratio")],
  content: [
    "./slices/main/web/**/*.html.slim",
    "./slices/main/assets/public/**/*.ts",
    "./slices/main/assets/public/**/*.tsx",
    "./slices/main/assets/public/**/*.js",
    "./slices/main/lib/view/**/*.rb",
  ],
  // Adding bg classes here because bg options set in types.rb don't include the bg- prefix
  safelist: [
    "bg-white",
    "bg-tan",
    "bg-black",
    "bg-purple-200",
    "bg-purple-200",
    "bg-purple-300",
    "bg-purple-400",
    "bg-purple-500",
    "[&[data-reveal='true']]:border-purple-400",
    "bg-jade-200",
    "bg-jade-300",
    "bg-jade-400",
    "bg-jade-500",
    "border-jade-200",
    "border-jade-300",
    "border-jade-400",
    "border-jade-500",
    "[&[data-reveal='true']]:border-jade-400",
    "border-orange-200",
    "border-orange-300",
    "border-orange-400",
    "border-orange-500",
    "[&[data-reveal='true']]:border-orange-400",
    "bg-blue-200",
    "bg-blue-300",
    "bg-blue-400",
    "bg-blue-500",
    "[&[data-reveal='true']]:border-blue-400",
    "bg-green-200",
    "bg-green-300",
    "bg-green-400",
    "bg-green-500",
    "bg-pink-200",
    "bg-pink-300",
    "bg-pink-400",
    "bg-pink-500",
    "bg-orange-200",
    "bg-orange-300",
    "bg-orange-400",
    "bg-orange-500",
    "bg-yellow-200",
    "bg-yellow-300",
    "bg-yellow-400",
    "bg-yellow-500",
  ],
}
